import * as React from 'react'
import * as ToastPrimitives from '@radix-ui/react-toast'
import { X } from 'lucide-react'

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef(({ className = '', ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={className || undefined}
    style={{
      position: 'fixed',
      bottom: 0,
      right: 0,
      top: 'auto',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '100vh',
      width: '100%',
      maxWidth: '26.25rem',
      padding: '1rem',
      zIndex: 100,
    }}
    {...props}
  />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const variantStyles = {
  default:     { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' },
  destructive: { background: 'color-mix(in srgb, var(--danger) 10%, var(--surface))', border: '1px solid color-mix(in srgb, var(--danger) 40%, transparent)', color: 'var(--danger)' },
  success:     { background: 'color-mix(in srgb, var(--success) 10%, var(--surface))', border: '1px solid color-mix(in srgb, var(--success) 40%, transparent)', color: 'var(--success)' },
  warning:     { background: 'color-mix(in srgb, var(--warning) 10%, var(--surface))', border: '1px solid color-mix(in srgb, var(--warning) 40%, transparent)', color: 'var(--warning)' },
}

const Toast = React.forwardRef(({ className = '', variant = 'default', style = {}, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={className || undefined}
      style={{
        position: 'relative',
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        overflow: 'hidden',
        borderRadius: 'var(--radius)',
        padding: '1.5rem',
        paddingRight: '2rem',
        boxShadow: 'var(--shadow)',
        transition: 'all 0.2s',
        ...variantStyles[variant] || variantStyles.default,
        ...style,
      }}
      {...props}
    />
  )
})
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = React.forwardRef(({ className = '', style = {}, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={className || undefined}
    style={{
      display: 'inline-flex',
      height: '2rem',
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 'var(--radius)',
      border: '1px solid var(--border)',
      background: 'transparent',
      padding: '0 0.75rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background 0.15s',
      ...style,
    }}
    {...props}
  />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef(({ className = '', style = {}, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={className || undefined}
    style={{
      position: 'absolute',
      right: '0.5rem',
      top: '0.5rem',
      borderRadius: 'var(--radius)',
      padding: '0.25rem',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: 'var(--text-muted)',
      opacity: 0.7,
      transition: 'opacity 0.15s',
      ...style,
    }}
    toast-close=""
    {...props}
  >
    <X style={{ width: '1rem', height: '1rem' }} />
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef(({ className = '', style = {}, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={['font-semibold', className].filter(Boolean).join(' ')}
    style={{ fontSize: '0.875rem', ...style }}
    {...props}
  />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef(({ className = '', style = {}, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={className || undefined}
    style={{ fontSize: '0.875rem', opacity: 0.9, ...style }}
    {...props}
  />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

export {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
}
